import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import papaparse from "papaparse";
import {
  Stack,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Alert,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import updatePOPricesFromCSV, {
  executePriceUpdates,
} from "../function/utilityAPI/updatePOPricesFromCSV";

export default function PriceUpdater() {
  const client = useApolloClient();
  const [csvText, setCsvText] = useState("");
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [results, setResults] = useState(null);
  const [updateResults, setUpdateResults] = useState(null);

  async function handleProcess() {
    if (!csvText.trim()) {
      alert("Please paste CSV content");
      return;
    }

    setLoading(true);
    setResults(null);
    setUpdateResults(null);

    try {
      // Parse CSV
      const { data, errors } = papaparse.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
      });

      if (errors.length > 0) {
        console.error("CSV parsing errors:", errors);
        alert("Error parsing CSV. Check console for details.");
        setLoading(false);
        return;
      }

      // Process CSV rows
      const processedResults = await updatePOPricesFromCSV({
        client,
        csvRows: data,
      });

      setResults(processedResults);
    } catch (error) {
      console.error("Error processing CSV:", error);
      alert(`Error processing CSV: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdate() {
    if (!results || results.found.length === 0) {
      alert("No POs to update");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to update ${results.found.length} PO prices?`
      )
    ) {
      return;
    }

    setUpdating(true);
    setUpdateResults(null);

    try {
      const updates = await executePriceUpdates({
        client,
        updates: results.found,
      });

      setUpdateResults(updates);

      const successCount = updates.filter((r) => r.success).length;
      const failCount = updates.filter((r) => !r.success).length;

      alert(
        `Update complete!\nSuccess: ${successCount}\nFailed: ${failCount}`
      );
    } catch (error) {
      console.error("Error updating prices:", error);
      alert(`Error updating prices: ${error.message}`);
    } finally {
      setUpdating(false);
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h4" color="primary">
          PO Price Updater
        </Typography>

        <Box>
          <Typography variant="body1" gutterBottom>
            Paste your CSV content below. The CSV should have columns: Code, PO number, Open Quantity, added charge
            <br />
            <strong>Note:</strong> The "added charge" column should contain the NEW price (not an amount to add).
          </Typography>
          <TextField
            label="Paste CSV Content"
            multiline
            rows={15}
            fullWidth
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={`Paste CSV content here. Example:
Code,PO number,Open Quantity,added charge
21286073,4519653456,148000,0.05823
90347431,4519425055,45000,0.4529`}
            sx={{ mt: 2 }}
          />
        </Box>

      <LoadingButton
        variant="contained"
        onClick={handleProcess}
        loading={loading}
        disabled={!csvText.trim()}
      >
        Process CSV
      </LoadingButton>

      {results && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Processing Results
          </Typography>

          <Stack spacing={2} mb={3}>
            <Alert severity="info">
              <Typography>
                <strong>Summary:</strong>
                <br />
                Total rows: {results.summary.totalRows}
                <br />
                Found: {results.found.length}
                <br />
                Not found: {results.summary.notFound}
                <br />
                Errors: {results.summary.errors}
              </Typography>
            </Alert>
          </Stack>

          {results.found.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                POs Ready to Update ({results.found.length})
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Item Number</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell align="right">Current Price</TableCell>
                      <TableCell align="right">Price Change</TableCell>
                      <TableCell align="right">New Price</TableCell>
                      <TableCell>Currency</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.found.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.po.number}</TableCell>
                        <TableCell>{item.po.itemNumber}</TableCell>
                        <TableCell>{item.po.code}</TableCell>
                        <TableCell align="right">
                          {item.po.currentPrice.toFixed(4)}
                        </TableCell>
                        <TableCell align="right">
                          {item.po.priceChange >= 0 ? "+" : ""}
                          {item.po.priceChange.toFixed(4)}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{item.po.newPrice.toFixed(5)}</strong>
                        </TableCell>
                        <TableCell>{item.po.priceCur || "N/A"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box mt={2}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={handleUpdate}
                  loading={updating}
                >
                  Confirm and Update {results.found.length} POs
                </LoadingButton>
              </Box>
            </Box>
          )}

          {results.notFound.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom color="warning.main">
                Not Found ({results.notFound.length})
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.notFound.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.row.Code}</TableCell>
                        <TableCell>{item.row["PO number"]}</TableCell>
                        <TableCell>{item.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {results.errors.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom color="error.main">
                Errors ({results.errors.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.errors.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.row.Code}</TableCell>
                        <TableCell>{item.row["PO number"]}</TableCell>
                        <TableCell>{item.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}

        {updateResults && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Update Results
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {updateResults.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.po.number}</TableCell>
                      <TableCell>{item.po.code}</TableCell>
                      <TableCell>
                        {item.success ? (
                          <Typography color="success.main">Success</Typography>
                        ) : (
                          <Typography color="error.main">Failed</Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.error || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Stack>
    </Container>
  );
}

